import type { DependencyList } from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'react-redux';
import isEqual from 'lodash/isEqual';
import type Luminary from '@lumen5/luminary';
import type {
    Asset,
    AssetType,
    Container,
    LuminaryObject,
    SceneGroup,
    Slide,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { GetAssetType } from '@lumen5/luminary/core/asset/Container';

import { useForceUpdate } from 'js/src/components/common/hooks/useForceUpdate';
import { useIsMounted } from 'js/src/components/common/hooks/useIsMounted';
import { useStateDeepCompare } from 'js/src/components/common/hooks/useStateDeepCompare';
import type { AppDispatch, RootState } from 'js/src/libs/RootState';
import { LuminaryContext, useLuminaryContext } from 'js/src/luminary/luminary-context-provider';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import type { ShallowObject } from 'js/src/types';
import { isShallowObject } from 'js/src/types';

export const useLuminaryLoaded = () => {
    const luminary = useLuminaryContext();
    const [luminaryLoaded, setLuminaryLoaded] = useState(false);
    useEffect(() => {
        const setLoadedOnAllImagesRendered = async () => {
            await luminary.onAllSlideImagesRendered();
            setLuminaryLoaded(true);
        };
        setLoadedOnAllImagesRendered().catch(() => {
            // do nothing
        });
    });
    return { luminary, luminaryLoaded };
};

type LuminaryHookOptions = {
    /**
     * The luminary events to subscribe to. If none provided, will subscribe to stateChanged.
     */
    events?: string[];
    /**
     * Optionally, the luminary instance to subscribe to events on. If not provided, the luminary
     * instance from the LuminaryContext will be used.
     */
    luminary?: Luminary;
};

const subscribeToLuminary = (luminary: Luminary, callback: () => void, events?: string[]) => {
    const eventList = events || ['stateChanged'];
    eventList.forEach((event) => {
        luminary.on(event, callback);
    });
    return () => {
        eventList.forEach((event) => {
            luminary.off(event, callback);
        });
    };
};

/**
 * Subscribe to Luminary events and force a React re-render when they occur.
 */
export const useLuminaryEvents = (opts: LuminaryHookOptions) => {
    const luminaryFromContext = useLuminaryContext();
    const luminary = opts.luminary || luminaryFromContext;
    const { forceUpdate } = useForceUpdate();

    useEffect(() => {
        return subscribeToLuminary(luminary, forceUpdate, opts.events);
    }, [luminary, opts?.events, forceUpdate]);
};

export const useLuminaryEffect = (
    effect: (luminary: Luminary) => void,
    deps: DependencyList,
    opts?: LuminaryHookOptions,
) => {
    const luminaryFromContext = useLuminaryContext();
    const luminary = opts?.luminary || luminaryFromContext;
    useEffect(() => {
        return subscribeToLuminary(
            luminary,
            () => {
                effect(luminary);
            },
            opts?.events,
        );
        // We don't want to include the selector function in the dependency list because it may
        // change on every render if it's defined inline. Instead, we rely on the deps list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [luminary, opts?.events, ...deps]);
};

type LuminaryObjects =
    | LuminaryObject
    | Asset
    | Container
    | LuminaryObject[]
    | Asset[]
    | Container[];
type NotLuminaryClasses<T> = Exclude<T, LuminaryObjects>;

function validateSelectorInDev<T>(value: T) {
    if (Array.isArray(value)) {
        value.forEach((entry) => {
            if (!isShallowObject(entry)) {
                throw new Error(
                    'The selector function must return a primitive or a shallow object. It cannot return a deep object or a Luminary class object.',
                );
            }
        });
    } else if (!isShallowObject(value)) {
        throw new Error(
            'The selector function must return a primitive or a shallow object. It cannot return a deep object or a Luminary class object.',
        );
    }
}

/**
 * Uses the luminary font manager to load fonts and figure out what the font name should be
 * for each font url passed in.
 * @param fontUrls a list of font urls
 * @returns a mapping of font urls to font names:
 *     {
 *         [fontUrl]: [fontName],
 *     }
 */
export const useFontUrlsToFontNames = (fontUrls: string[]) => {
    const { updateTrigger, forceUpdate } = useForceUpdate();
    const fontManager = VideoData.getGlobalFontManager();

    const prevFontUrlsRef = useRef(fontUrls);
    const stableFontUrls = useMemo(() => {
        if (isEqual(prevFontUrlsRef.current, fontUrls)) {
            return prevFontUrlsRef.current;
        }
        prevFontUrlsRef.current = fontUrls;
        return fontUrls;
    }, [fontUrls]);

    useEffect(() => {
        const onLoad = () => {
            forceUpdate();
        };

        fontManager.on('loaded', onLoad);

        return () => {
            fontManager.off('loaded', onLoad);
        };
    }, [fontManager, forceUpdate]);

    useEffect(() => {
        stableFontUrls.forEach((fontUrl) => {
            if (!fontManager.getFontBySource(fontUrl)) {
                fontManager.addFont(fontUrl);
            }
        });
    }, [fontManager, stableFontUrls]);

    return useMemo(
        () =>
            stableFontUrls.reduce(
                (acc, fontUrl) => {
                    acc[fontUrl] = fontManager.getFullNameOfLoadedFont(fontUrl);
                    return acc;
                },
                {} as Record<string, string>,
            ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [fontManager, stableFontUrls, updateTrigger],
    );
};

type LuminarySelectorOptions = LuminaryHookOptions & {
    /**
     * If provided, the selector will only run if the scene group exists and is not destroyed.
     */
    sceneGroupId?: string;
    /**
     * If provided, the selector will only run if the scene exists and is not destroyed.
     */
    sceneId?: string;
    /**
     * If provided, the selector will only run if the asset exists and is not destroyed.
     */
    assetId?: string;
};

/**
 * A hook that subscribes to luminary changes and returns the value of the selector function.
 * By default, the hook will subscribe to the "stateChanged" event but can be configured to subscribe
 * to other events.
 *
 * You must pass a dependency list to the hook so that it knows when to re-evaluate the selector
 * if a dependency changes. (Dependencies are closure variables used within the selector that
 * come from the outer context and can change value.) An empty array means no dependencies.
 *
 * It's important to not do any state changes or "writes" to luminary within the selector.
 * If you need to do changes to luminary then use useLuminaryOperation.
 *
 * The selector function cannot return a "luminary object", like Scene, because those objects
 * are mutable and can change without triggering a re-render. Instead, the selector function
 * should return a primitive or a shallow object.
 *
 * If you want to select multiple values at once, you can return an array or object. Note though
 * that currently the selector will not allow you to return a nested object. This is to avoid
 * creating very deep objects which could be slow to compare inside the selector. If this use case
 * comes up we can re-evaluate at that time though if needed. You may return an array of shallow
 * objects though.
 */
export const useLuminarySelector = <T extends ShallowObject | ShallowObject[]>(
    selector: (luminary: Readonly<Luminary>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts?: LuminarySelectorOptions,
): NotLuminaryClasses<T> => {
    const luminaryFromContext = useContext(LuminaryContext);
    const luminary = opts?.luminary || luminaryFromContext;
    if (!luminary) {
        throw new Error(
            'useLuminarySelector must be used within a LuminaryContextProvider or be provided a luminary instance',
        );
    }
    const [value, setValue] = useStateDeepCompare(selector(luminary));
    const selectorRef = useRef(selector);
    const isMounted = useIsMounted();

    /**
     * This effect handles the case where the selector function changes. We need to re-compute
     * the value since a value in the selector function may have changed.
     * This also stores the selector function in a ref so the other effect can use the latest
     * version of the selector function without recreating its subscriptions.
     */
    useEffect(() => {
        if (!shouldRunSelector(luminary, opts?.sceneGroupId, opts?.sceneId, opts?.assetId)) {
            return;
        }
        const newValue = selector(luminary);
///////////////////
////////////////////////////////////////
//////////////////
        setValue(newValue);
        selectorRef.current = selector;
        // We don't want to include the selector function in the dependency list because it may
        // change on every render if it's defined inline. Instead, we rely on the deps list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [luminary, setValue, opts?.sceneGroupId, opts?.sceneId, opts?.assetId, ...deps]);

    /**
     * This effect handles the case where luminary emits an event. We need to re-compute the value
     * since luminary may have changed.
     */
    useEffect(() => {
        const onChange = () => {
            if (!isMounted.current) {
                // Even though we called the unsubscribe function during unmount, the callbacks
                // may still run. See https://github.com/primus/eventemitter3/issues/262
                return;
            }
            if (!shouldRunSelector(luminary, opts?.sceneGroupId, opts?.sceneId, opts?.assetId)) {
                return;
            }

            const newValue = selectorRef.current(luminary);
///////////////////////
////////////////////////////////////////////
//////////////////////
            setValue(newValue);
        };
        return subscribeToLuminary(luminary, onChange, opts?.events);
    }, [
        luminary,
        setValue,
        isMounted,
        opts?.sceneGroupId,
        opts?.sceneId,
        opts?.assetId,
        opts?.events,
    ]);

    return value!;
};

/**
 * If the selector filters for a specific scene group, scene, or asset, then we only
 * want to run the selector if the filtered object exists and is not destroyed.
 */
function shouldRunSelector(
    luminary: Luminary,
    sceneGroupId?: string,
    sceneId?: string,
    assetId?: string,
): boolean {
    let sceneGroup: SceneGroup | undefined;
    let scene: Slide | undefined;
    if (sceneGroupId) {
        sceneGroup = luminary.getSceneGroupById(sceneGroupId);
        if (!sceneGroup || sceneGroup.destroyed) {
            return false;
        }
    }
    if (sceneId) {
        scene = luminary.getSceneById(sceneId);
        if (!scene || scene.destroyed) {
            return false;
        }
    }
    if (assetId) {
        const asset = sceneGroup ? sceneGroup.getAssetById(assetId) : scene?.getAssetById(assetId);
        if (!asset || asset.destroyed) {
            return false;
        }
    }
    return true;
}

type SceneGroupSelectorOptions = LuminaryHookOptions & {
    sceneGroupId: string;
};

/**
 * A convenience luminary selector for selecting a scene group by id.
 *
 * Refer to useLuminarySelector for more details.
 */
export const useLuminarySceneGroupSelector = <T extends ShallowObject>(
    selector: (sceneGroup: Readonly<SceneGroup>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneGroupSelectorOptions,
): NotLuminaryClasses<T> => {
    return useLuminarySelector(
        (luminary) => {
            const sceneGroup = luminary.getSceneGroupById(opts.sceneGroupId);
            if (!sceneGroup) {
                // We're not sure yet the best way to handle this. So if you come across this, it would
                // be good to discuss with the team. Here was the discussion on the original PR:
                // https://github.com/Lumen5/web/pull/11117#discussion_r1291841040
                throw new Error('luminary scene group selector could not find scene group');
            }
            return selector(sceneGroup);
            // We don't want to include the selector function in the dependency list because it may
            // change on every render if it's defined inline. Instead, we rely on the deps list.
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [opts.sceneGroupId, ...deps],
        opts,
    );
};

type SceneSelectorOptions = LuminaryHookOptions & {
    sceneId: string;
};

/**
 * A convenience luminary selector for selecting a scene by id.
 *
 * Refer to useLuminarySelector for more details.
 */
export const useLuminarySceneSelector = <T extends ShallowObject>(
    selector: (sceneGroup: Readonly<Slide>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: SceneSelectorOptions,
): NotLuminaryClasses<T> => {
    throw new Error(
        'useLuminarySceneSelector is not safe for use. See https://www.notion.so/lumen5/Technical-Directions-25-Scratch-Pad-143479b81546808d8c9ffe618ead9c76?pvs=4#143479b81546809cac92e881a8c56b10',
    );
    // return useLuminarySelector(luminary => {
    //     const scene = luminary.getSceneById(opts.sceneId);
    //     if (!scene) {
    //         // We're not sure yet the best way to handle this. So if you come across this, it would
    //         // be good to discuss with the team. Here was the discussion on the original PR:
    //         // https://github.com/Lumen5/web/pull/11117#discussion_r1291841040
    //         throw new Error('luminary scene selector could not find scene');
    //     }
    //     return selector(scene);
    //     // We don't want to include the selector function in the dependency list because it may
    //     // change on every render if it's defined inline. Instead, we rely on the deps list.
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [opts.sceneId, ...deps], opts);
};

type AssetSelectorOptions<T extends AssetType> = LuminaryHookOptions & {
    sceneGroupId?: string;
    sceneId?: string;
    assetId: string;
    assetType: T;
} & ({ sceneGroupId: string; sceneId?: never } | { sceneGroupId?: never; sceneId: string });

/**
 * A convenience luminary selector for selecting an asset by id. You need to also provide either
 * a sceneGroupId or sceneId to indicate which scene or scene group to look in.
 *
 * Refer to useLuminarySelector for more details.
 */
export const useLuminaryAssetSelector = <T extends ShallowObject, A extends AssetType>(
    selector: (asset: GetAssetType<A>) => NotLuminaryClasses<T>,
    deps: DependencyList,
    opts: AssetSelectorOptions<A>,
): NotLuminaryClasses<T> => {
    throw new Error(
        'useLuminaryAssetSelector is not safe for use. See https://www.notion.so/lumen5/Technical-Directions-25-Scratch-Pad-143479b81546808d8c9ffe618ead9c76?pvs=4#143479b81546809cac92e881a8c56b10',
    );
    // return useLuminarySelector(
    //     (luminary) => {
    //         let sceneOrGroup: SceneGroup | Slide | null | undefined = null;
    //         if (opts.sceneGroupId) {
    //             sceneOrGroup = luminary.getSceneGroupById(opts.sceneGroupId);
    //         } else if (opts.sceneId) {
    //             sceneOrGroup = luminary.getSceneById(opts.sceneId);
    //         }
    //         if (!sceneOrGroup) {
    //             // We're not sure yet the best way to handle this. So if you come across this, it would
    //             // be good to discuss with the team. Here was the discussion on the original PR:
    //             // https://github.com/Lumen5/web/pull/11117#discussion_r1291841040
    //             ErrorReporter.setContext('asset selector', { opts });
    //             throw new Error('luminary asset selector could not find scene or scene group');
    //         }
    //         const asset = sceneOrGroup.getAssetById(opts.assetId);
    //         if (!asset) {
    //             // We're not sure yet the best way to handle this. So if you come across this, it would
    //             // be good to discuss with the team. Here was the discussion on the original PR:
    //             // https://github.com/Lumen5/web/pull/11117#discussion_r1291841040
    //             ErrorReporter.setContext('asset selector', { opts });
    //             throw new Error('luminary asset selector could not find asset');
    //         }
    //         return selector(asset as GetAssetType<A>);
    //         // We don't want to include the selector function in the dependency list because it may
    //         // change on every render if it's defined inline. Instead, we rely on the deps list.
    //     },
    //     // eslint-disable-next-line react-hooks/exhaustive-deps
    //     [opts.sceneGroupId, opts.sceneId, opts.assetId, ...deps],
    //     opts,
    // );
};

export const _luminaryOperationSymbol = Symbol.for('luminaryOperationSymbol');

type MakeOperationNotCallable<T> = {
    [_luminaryOperationSymbol]: T;
};

export type MakeOperationsCallable<NCO extends RecordOfNotCallableOperations> =
    keyof NCO extends never
        ? NonNullable<unknown>
        : {
              [O in keyof NCO extends `${infer N}Operation`
                  ? N
                  : never]: NCO[`${O}Operation`] extends NotCallableOperation<infer iP, infer iR>
                  ? (...params: iP) => iR
                  : never;
          };

export type MakeOperationCallable<NCO extends NotCallableOperation<any, any>> =
    NCO extends NotCallableOperation<infer iP, infer iR> ? (...params: iP) => iR : never;

type LuminaryOverrideArg = { luminary: Luminary };
type LuminaryListOverrideArg = { luminaryList: Luminary[] };

type GetNonCallableOperationParams<OP> = OP extends NotCallableOperation<infer P, any> ? P : never;
type GetNonCallableOperationReturn<OP> = OP extends NotCallableOperation<any, infer R> ? R : never;

export type OperationCall = {
    <OP>(op: OP, ...args: GetNonCallableOperationParams<OP>): GetNonCallableOperationReturn<OP>;
    <OP>(
        op: OP,
        ...args: [...GetNonCallableOperationParams<OP>, { luminary: Luminary }]
    ): GetNonCallableOperationReturn<OP>;
    <OP>(
        op: OP,
        ...args: [...GetNonCallableOperationParams<OP>, { luminaryList: Luminary[] }]
    ): GetNonCallableOperationReturn<OP>;
};

type CallableOperation<P extends Array<any>, R> = (
    config: {
        dispatch: AppDispatch;
        luminary: Luminary;
        getState: GetRootState;
        call: OperationCall;
    },
    ...params: P
) => R;

export type NotCallableOperation<P extends Array<any>, R> = MakeOperationNotCallable<
    CallableOperation<P, R>
>;

export type RecordOfNotCallableOperations = {
    [O: `${string}Operation`]: NotCallableOperation<any, any>;
};

/**
 * Creates a luminary operation. This is used to create operations that cannot be called directly,
 * but instead must be called through useLuminaryOperations.
 *
 * @param callback - required function that defines the luminary operation
 * @param requiredOperations - optional object of other luminary operations which are to be used by this one.
 */
export function createLuminaryOperation<Params extends Array<any>, Ret>(
    callback: CallableOperation<Params, Ret>,
): MakeOperationNotCallable<CallableOperation<Params, Ret>> {
    return {
        [_luminaryOperationSymbol]: callback,
    };
}

export function createCall<Params extends any[], Ret, T extends NotCallableOperation<Params, Ret>>({
    luminary,
    dispatch,
    getState,
}: {
    luminary: Luminary;
    dispatch: AppDispatch;
    getState: GetRootState;
}): {
    (notCallableOperation: T, ...args: [...Params]): Ret;
    (notCallableOperation: T, ...args: [...Params, LuminaryOverrideArg]): Ret;
    (notCallableOperation: T, ...args: [...Params, LuminaryListOverrideArg]): Ret[];
} {
    return ((notCallableOperation, ...args) => {
        let argsToPass: Params;
        let luminaryInstanceToPass: Luminary;
        let luminaryListToPass: Luminary[] | undefined;
        if (
            args.length &&
            args[args.length - 1] &&
            ((args[args.length - 1] as LuminaryOverrideArg).luminary ||
                (args[args.length - 1] as LuminaryListOverrideArg).luminaryList)
        ) {
            argsToPass = args.slice(0, -1) as Params;
            luminaryInstanceToPass = (args[args.length - 1] as LuminaryOverrideArg).luminary;
            luminaryListToPass = (args[args.length - 1] as LuminaryListOverrideArg).luminaryList;
        } else {
            argsToPass = args as Params;
            luminaryInstanceToPass = luminary;
            luminaryListToPass = undefined;
        }

        const callableOperation = notCallableOperation[_luminaryOperationSymbol];
        if (luminaryListToPass) {
            return luminaryListToPass.map((luminaryInstance) =>
                callableOperation(
                    {
                        luminary: luminaryInstance,
                        dispatch,
                        getState,
                        call: createCall({
                            luminary: luminaryInstance,
                            dispatch,
                            getState,
                        }),
                    },
                    ...argsToPass,
                ),
            );
        }

        return callableOperation(
            {
                luminary: luminaryInstanceToPass,
                dispatch,
                getState,
                call: createCall({
                    luminary: luminaryInstanceToPass,
                    dispatch,
                    getState,
                }),
            },
            ...argsToPass,
        );
    }) as {
        (notCallableOperation: T, ...args: [...Params]): Ret;
        (notCallableOperation: T, ...args: [...Params, LuminaryOverrideArg]): Ret;
        (notCallableOperation: T, ...args: [...Params, LuminaryListOverrideArg]): Ret[];
    };
}

export function makeOperationsCallable<T extends RecordOfNotCallableOperations>(
    nonCallableOperations: T,
    luminary: Luminary,
    dispatch: AppDispatch,
    getState: GetRootState,
): MakeOperationsCallable<T> {
    return Object.fromEntries(
        Object.entries(nonCallableOperations).map(([name, notCallableOperation]) => [
            name.replace('Operation', ''),
            (...args: any) => {
                const callableOperation = notCallableOperation[_luminaryOperationSymbol];
                return callableOperation(
                    {
                        luminary,
                        dispatch,
                        getState,
                        call: createCall({ luminary, dispatch, getState }),
                    },
                    ...args,
                );
            },
        ]),
    ) as MakeOperationsCallable<T>;
}

type LuminaryOperationOptions = {
    /**
     * Optionally, the luminary instance to perform actions on. If not provided, the luminary
     * instance from the LuminaryContext will be used.
     */
    luminary?: Luminary;
};

/**
 * A hook for using luminary operations. This hook is similar to useLuminarySelector, but instead
 * of reading a value from luminary, it allows you to call a function (or perform a write action)
 * on luminary.
 * It also passes the redux state and dispatch function to the operation, so you can
 * read and write to the redux store as well.
 *
 * Operations passed to this hook must be created with createLuminaryOperation. This is to ensure
 * that operations are not called directly, but instead are called through this hook.
 *
 * This will return an object of callable operations (functions) that have the same name as the
 * operations passed in, but without the "Operation" suffix.
 */
export const useLuminaryOperations = <T extends RecordOfNotCallableOperations>(
    operations: T,
    opts?: LuminaryOperationOptions,
): MakeOperationsCallable<T> => {
    const luminaryFromContext = useContext(LuminaryContext);
    const luminary = opts?.luminary || luminaryFromContext;
    if (!luminary) {
        throw new Error(
            'useLuminaryOperations must be used within a LuminaryContextProvider or be provided a luminary instance',
        );
    }
    const store = useStore<RootState>();

    return useMemo(() => {
        return makeOperationsCallable(operations, luminary, store.dispatch, store.getState);
        // operations will never change, so we don't need to recompute this, however since
        // we pass in operations as an object, it will always be a new object each render, so
        // we need to disable the exhaustive deps rule.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [luminary, store.dispatch, store.getState]);
};
